import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { contactsApi } from '../../../api/adminApi';
import './ContactList.css';

const DEFAULT_LIMIT = 10;
const CONTACT_TYPES = [
  { value: 'address', label: 'Address' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'hours', label: 'Hours' },
  { value: 'social', label: 'Social Link' },
  { value: 'other', label: 'Other' },
];

function ContactList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formType, setFormType] = useState('address');
  const [formLabel, setFormLabel] = useState('');
  const [formValue, setFormValue] = useState('');
  const [formDisplayOrder, setFormDisplayOrder] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadContacts = (pageNum = page) => {
    setLoading(true);
    setError('');
    contactsApi
      .list(pageNum, limit)
      .then((res) => {
        setContacts(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load contacts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadContacts(1);
  }, []);

  useEffect(() => {
    const editContact = location.state?.editContact;
    if (editContact) {
      navigate(location.pathname, { replace: true, state: {} });
      openEditModal(editContact);
    }
  }, [location.state?.editContact]);

  const handleDeleteClick = (contact) => setDeleteId(contact);

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    setDeleting(true);
    contactsApi
      .delete(deleteId.id)
      .then(() => {
        setDeleteId(null);
        loadContacts(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const handleDeleteCancel = () => setDeleteId(null);

  const openAddModal = () => {
    setEditingContact(null);
    setFormType('address');
    setFormLabel('');
    setFormValue('');
    setFormDisplayOrder('');
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setFormType(contact?.type ?? contact?.contact_type ?? 'address');
    setFormLabel(contact?.label ?? contact?.name ?? '');
    setFormValue(contact?.value ?? contact?.content ?? contact?.url ?? '');
    setFormDisplayOrder(contact?.display_order != null ? String(contact.display_order) : '');
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingContact(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formValue.trim()) {
      setFormError('Value is required.');
      return;
    }
    setFormSaving(true);
    const fields = {
      type: formType,
      label: formLabel.trim() || undefined,
      value: formValue.trim(),
      display_order: formDisplayOrder === '' ? undefined : Number(formDisplayOrder),
    };
    const promise = editingContact
      ? contactsApi.update(editingContact.id, fields)
      : contactsApi.create(fields);
    promise
      .then(() => {
        closeFormModal();
        loadContacts(page);
      })
      .catch((err) => {
        setFormError(err.userMessage || 'Something went wrong.');
      })
      .finally(() => setFormSaving(false));
  };

  const contactType = (c) => c.type ?? c.contact_type ?? '—';
  const contactLabel = (c) => c.label ?? c.name ?? '—';
  const contactValue = (c) => (c.value ?? c.content ?? c.url ?? '—').slice(0, 60) + ((c.value ?? c.content ?? c.url ?? '').length > 60 ? '…' : '');
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="admin-contacts">
      <div className="admin-contacts-header">
        <h2>Contact Details</h2>
        <button type="button" className="admin-contacts-add" onClick={openAddModal}>
          Add contact
        </button>
      </div>

      {error && (
        <div className="admin-contacts-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-contacts-loading">Loading contacts…</div>
      ) : (
        <div className="admin-contacts-table-wrap">
          <table className="admin-contacts-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Label</th>
                <th>Value</th>
                <th>Order</th>
                <th className="admin-contacts-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-contacts-empty">
                    No contacts yet. <button type="button" className="admin-contacts-empty-btn" onClick={openAddModal}>Add one</button>.
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr key={c.id}>
                    <td>{contactType(c)}</td>
                    <td>{contactLabel(c)}</td>
                    <td className="admin-contacts-value">{contactValue(c)}</td>
                    <td>{c.display_order ?? c.displayOrder ?? '—'}</td>
                    <td className="admin-contacts-actions">
                      <Link to={`/admin/contacts/${c.id}`} className="admin-contacts-btn admin-contacts-btn-view">
                        View
                      </Link>
                      <button
                        type="button"
                        className="admin-contacts-btn admin-contacts-btn-edit"
                        onClick={() => openEditModal(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-contacts-btn admin-contacts-btn-delete"
                        onClick={() => handleDeleteClick(c)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-contacts-pagination">
          <button
            type="button"
            className="admin-contacts-page-btn"
            onClick={() => loadContacts(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-contacts-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-contacts-page-btn"
            onClick={() => loadContacts(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={closeFormModal}
        contentLabel={editingContact ? 'Edit contact' : 'Add contact'}
        className="admin-contacts-modal"
        overlayClassName="admin-contacts-modal-overlay"
      >
        <div className="admin-contacts-modal-inner">
          <h3>{editingContact ? 'Edit contact' : 'Add contact'}</h3>
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-contacts-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="admin-contacts-form-field">
              <label htmlFor="form-contact-type">Type *</label>
              <select
                id="form-contact-type"
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                disabled={formSaving}
              >
                {CONTACT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="admin-contacts-form-field">
              <label htmlFor="form-contact-label">Label</label>
              <input
                id="form-contact-label"
                type="text"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder="e.g. Main office"
                disabled={formSaving}
              />
            </div>
            <div className="admin-contacts-form-field">
              <label htmlFor="form-contact-value">Value *</label>
              <input
                id="form-contact-value"
                type="text"
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                placeholder="e.g. 72 Clarkson Ave, Brooklyn NY"
                disabled={formSaving}
              />
            </div>
            <div className="admin-contacts-form-field">
              <label htmlFor="form-contact-order">Display order</label>
              <input
                id="form-contact-order"
                type="number"
                min={0}
                value={formDisplayOrder}
                onChange={(e) => setFormDisplayOrder(e.target.value)}
                placeholder="0"
                disabled={formSaving}
              />
            </div>
            <div className="admin-contacts-form-actions">
              <button type="button" className="admin-contacts-form-cancel" onClick={closeFormModal} disabled={formSaving}>
                Cancel
              </button>
              <button type="submit" className="admin-contacts-form-submit" disabled={formSaving}>
                {formSaving ? (editingContact ? 'Updating…' : 'Creating…') : (editingContact ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteId && (
        <div className="admin-contacts-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-contact-title">
          <div className="admin-contacts-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-contacts-delete-box">
            <h3 id="delete-contact-title">Delete contact?</h3>
            <p>This will permanently delete this contact detail. This cannot be undone.</p>
            <div className="admin-contacts-delete-actions">
              <button type="button" className="admin-contacts-btn admin-contacts-btn-edit" onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="admin-contacts-btn admin-contacts-btn-delete" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactList;
